import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { buildSupportReport, SupportFacts } from "../utils/buildSupportReport";
import { getSupportContext, SupportSignals } from "../utils/getSupportContext";
import {
  getSupportChannels,
  SupportChannelConfig,
} from "../utils/supportChannels";
import getTestId from "../utils/getTestId";

type ContactSupportProps = {
  signals?: SupportSignals;
  facts?: SupportFacts;
  config: SupportChannelConfig;
  /*
   * `inherit` is the default and must stay it. errorPage renders this on a
   * dark tenant-blue surface where MUI's primary came out at a 1.20 contrast
   * ratio -- the control drew and was invisible. `brand` is opt-in, for hosts
   * that place these in a light button stack and need them to match it.
   */
  tone?: "inherit" | "brand";
  // The transaction screens stack these under their own buttons, where a lead
  // line between controls reads as clutter rather than help.
  showPrompt?: boolean;
};

/*
 * Offered where a failure is shown, so the payer never has to describe which
 * screen they were on. Both channels are presented together and the payer
 * picks; the app does not choose for them, because a tracked ticket and a
 * chat suit different people and we cannot tell which is which.
 */
const ContactSupport = ({
  signals,
  facts,
  config,
  tone = "inherit",
  showPrompt = true,
}: ContactSupportProps) => {
  const { t, i18n } = useTranslation();

  const context = getSupportContext(signals);
  const report = buildSupportReport(
    context,
    { locale: i18n.language, ...facts },
    key => t(key, { defaultValue: key }) as string
  );
  const channels = getSupportChannels(report, config);

  // No channel configured is a deployment that has not enabled support, not a
  // broken screen. Rendering a dead control would be worse than rendering
  // nothing -- this is the last thing a frustrated payer touches.
  if (channels.length === 0) return null;

  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      {showPrompt && (
        <Typography variant="body2">{t("support.prompt")}</Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {channels.map(channel => (
          <Button
            key={channel.id}
            data-testid={getTestId({ label: `contact support ${channel.id}` })}
            href={channel.href}
            target="_blank"
            fullWidth
            size="large"
            // An external handoff opened in a new tab: noopener so the target
            // cannot reach back into this one while a payment is in progress.
            rel="noopener noreferrer"
            variant="outlined"
            /*
             * `inherit`, not `primary`. This renders on backgrounds the
             * component does not choose: on the error page MUI's primary blue
             * came out at a contrast ratio of 1.20 against the brand blue
             * behind it, so the control rendered and was invisible. Inheriting
             * currentColor tracks whatever the host set its text to, which is
             * the only thing here that is guaranteed to be readable on it.
             */
            color={tone === "brand" ? "primary" : "inherit"}
          >
            {t(`support.channel.${channel.id}`)}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default ContactSupport;
